import AppBar from '@material-ui/core/AppBar';
import Badge from '@material-ui/core/Badge';
import CssBaseline from '@material-ui/core/CssBaseline';
import IconButton from '@material-ui/core/IconButton';
import { createStyles, StyledComponentProps, Theme, withStyles } from '@material-ui/core/styles';
import Toolbar from '@material-ui/core/Toolbar';
import MailIcon from '@material-ui/icons/Mail';
import MenuIcon from '@material-ui/icons/Menu';
import Settings from '@material-ui/icons/Settings';
import classNames from 'classnames';
import { computed } from 'mobx';
import { observer } from 'mobx-react';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import * as React from 'react';
import { ErrorBoundary } from '../comps/ErrorBoundary';
import { Submenu } from '../comps/Submenu';
import { TesterDialog } from '../comps/TesterDialog';
import { LoginDialog } from '../dialogs/LoginDialog';
import { SetPasswordDialog } from '../dialogs/SetPasswordDialog';
import { Lifecycle } from '../lifecycle/Lifecycle';
import { toasterSvc } from '../services/ToasterSvc';
import { getOmnistore, useOmnistore } from '../stores/Omnistore';
import { MainpanelMenu } from './MainpanelMenu';
import { Sidebar, sidebarStore } from './Sidebar';
import { SubmenuProvider } from './useSubmenu';

const drawerWidth = 240;
const marginTop = 50;

const styles = (theme: Theme) => createStyles({
	root: {
		display: 'flex',
		height: '100%',
	},
	appBar: {
		zIndex: theme.zIndex.drawer + 1,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.leavingScreen,
		}),
		color: '#222222',
		backgroundColor: '#EEEEEE',
	},
	appBarShift: {
		marginLeft: drawerWidth,
		width: `calc(100% - ${drawerWidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	},
	isIdle: {
		color: '#777',
	},
	menuButton: {
		marginLeft: 12,
		marginRight: 36,
	},
	hide: {
		display: 'none',
	},
	badge: {
		marginLeft: '10px',
		marginRight: '10px',
	},
	toolbar: {
		display: 'flex',
		alignItems: 'left',
		...theme.mixins.toolbar,
		justifyContent: 'space-between',
	},
	closers: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'space-between',
	},
	logo: {
		display: 'block',
		width: '8em',
		height: '8em',
		margin: 0,
		marginTop: '0.5em',
	},
	content: {
		flex: 1,
		padding: theme.spacing(2),
		marginTop: marginTop,
		height: `calc(100% - ${marginTop}px)`,
	},
	screen: {
		overflow: 'hidden',
	},
});

const Main = observer(function(props: {classes: any}) {
	const omnistore = useOmnistore();
	const Body = omnistore.locationStore.componentClass;
	if (!Body) return null;
	return <main className={props.classes.content}>
		<ErrorBoundary>
			<SubmenuProvider>
				<div id="screen" className={props.classes.screen}>
					<Submenu/>
					<Body/>
				</div>
			</SubmenuProvider>
		</ErrorBoundary>
	</main>;
});

interface IMainpanelProps extends WithSnackbarProps {
}

@observer export class _Mainpanel extends React.Component<IMainpanelProps & StyledComponentProps> {
	@computed get authStore() {
		return getOmnistore().authStore;
	}

	@computed get reoctoStore() {
		return getOmnistore().reoctoStore;
	}

	@computed get profileStore() {
		return getOmnistore().profileStore;
	}

	state = {
		open: true,
		alive: true,
	};

	_subscriptions() {
		switch (this.authStore.role) {
		case 'STANDARD': return ['Branch', 'Message'];
		case 'HARMONIZER': return ['Supplier'];
		case 'ADMIN': return [];
		}
		return [];
	}

	componentDidMount() {
		toasterSvc.init(this.props);
		getOmnistore().entityManagerStore.subscribe('Mainpanel', () => this._subscriptions());
	}

	componentWillUnmount() {
		getOmnistore().entityManagerStore.unsubscribe('Mainpanel');
	}

	@computed get locationStore() {
		return getOmnistore().locationStore;
	}
	getUserNameLetter = () => {
		const userInfo = this.profileStore.ownUser;
		var userLetter = '';
		if (userInfo) {
			userLetter += userInfo['firstname'] ? userInfo['firstname'].charAt(0).toUpperCase(): '';
			userLetter += userInfo['surname'] ? userInfo['surname'].charAt(0).toUpperCase() : '';
		}
		return userLetter;
	};

	getUserInfoEmail = () => {
		const userInfo = this.profileStore.ownUser;
		if (userInfo) {
			return userInfo.email;
		}
		return '';
	};

	setPageProfile = () => this.locationStore.setPage('Profile')

	setPageSettings = () => this.locationStore.setPage('Settings')

	setPageMessages = () => this.locationStore.setPage('MessagePage')

	@computed get _nUnreadMessages() {
		const multirepo = getOmnistore().multirepo;
		return multirepo.messageRepo.allAsList.filter(e => !e.isRead).length;
	}

	render() {
		const classes = this.props.classes || {};
		const store = sidebarStore;
		const userLetters = this.getUserNameLetter();
		const userEmail = this.getUserInfoEmail();
		const lang = 'de'; //TODO

		return <div className={classes.root} lang={lang}>
			<CssBaseline/>
			<Lifecycle/>
			<AppBar position="fixed" className={classNames(classes.appBar,
				{[classes.appBarShift as any]: store.isOpen},
				{[classes.isIdle as any]: this.reoctoStore.isIdle})}>
				<Toolbar style={{paddingRight: '10px'}} disableGutters={!store.isOpen}>
					<IconButton
						color="inherit"
						onClick={store.open}
						className={classNames(classes.menuButton, {[classes.hide as any]: store.isOpen})}
					>
						<MenuIcon/>
					</IconButton>
					{this.locationStore.breadcrumbs}
					{/* {this.locationStore.page} */}
					<div style={{display: 'flex', flex: 1}}></div>
					<div style={{display: 'flex', flex: 1}}></div>
					{
						this.authStore.role!=='STANDARD' ? null :
							<Badge className={classes.badge} color="secondary" badgeContent={this._nUnreadMessages}
								onClick={this.setPageMessages}>
								<MailIcon/>
							</Badge>
					}
					{!this.reoctoStore.isTester ? null : <Settings className={classes.badge} onClick={this.setPageSettings}/>}
					<MainpanelMenu userNameLetters={userLetters} userEmail={userEmail} showProfilePage={this.setPageProfile} />
				</Toolbar>
			</AppBar>
			<Sidebar store={store}/>
			<Main classes={classes}/>
			<ErrorBoundary>
				<TesterDialog/>
				<LoginDialog/>
				{this.locationStore.secret ? <SetPasswordDialog locationStore={this.locationStore}/> : null}
			</ErrorBoundary>
		</div>;
	}
}

export const Mainpanel = withStyles(styles)(withSnackbar(_Mainpanel));
